import Api from '@/models/Api'

/**
 * Data contract for the two pre-cached Disney Delivery charts (separate from the
 * heavy `overview` call so each is its own request):
 *   GET delivery-dashboards/monthly-book        -> MonthlyBookResponse
 *   GET delivery-dashboards/daily-game-delivery -> DailyGameDeliveryResponse
 *
 * Both are served from companies.metrics['delivery_dashboard'] (pre-computed by
 * the daily distribution-goal update). Rates are fractions (0..1). ?refresh=1
 * forces a synchronous recompute on the backend.
 */

export interface MonthlyBookRow {
  month?: string // YYYY-MM (absent on the total row)
  label: string // e.g. "Mar" or "1H Total"
  requested: number // sold (target_goal) valued at inventory rate — US$, modeled per-month
  booked: number // active book (active_goal) valued — US$, modeled per-month
  delivered: number // real delivered revenue — US$
  delivered_booked_rate: number | null
  delivered_sold_rate: number | null
  booked_sold_rate: number | null
}

export interface MonthlyBookResponse {
  generated_at: string | null
  period: { start: string; end: string } | null
  months: MonthlyBookRow[]
  total?: MonthlyBookRow | null // legacy 1H total (kept for backward-compat)
  totals: MonthlyBookRow[] // 1H / 2H / Total summary columns
}

export interface DailyGameDeliveryDay {
  date: string // YYYY-MM-DD
  games: number
  requests: number
  impressions: number
  requests_per_game: number | null // null when no games that day
  impressions_per_game: number | null
  delivery_rate: number | null // impressions / requests
}

export interface DailyGameDeliveryResponse {
  generated_at: string | null
  window: { days: number; start: string; end: string } | null
  days: DailyGameDeliveryDay[]
}

// Dedupe the in-flight burst per endpoint so co-mounted widgets share one call;
// not persisted past resolution, so Refresh re-fetches.
const inFlight: Record<string, Promise<any> | null> = {}

/** True when the dashboard URL carries ?refresh=1 (history or hash routing). */
const refreshRequestedFromUrl = (): boolean => {
  const { search, hash } = window.location
  const qs = search || (hash.includes('?') ? hash.slice(hash.indexOf('?')) : '')
  const value = new URLSearchParams(qs).get('refresh')
  return value === '1' || value === 'true'
}

function fetchChart<T>(endpoint: string, refresh: boolean): Promise<T> {
  if (!inFlight[endpoint]) {
    const params: any = {}
    if (refresh || refreshRequestedFromUrl()) params.refresh = 1

    inFlight[endpoint] = new Api()
      .get(endpoint, params)
      .then((r: any) => r.data.result)
      .finally(() => {
        inFlight[endpoint] = null
      })
  }
  return inFlight[endpoint] as Promise<T>
}

/**
 * Round a value up to a clean axis max (1 / 1.5 / 2 / 2.5 / 5 / 10 × 10^n).
 * Used to pin the stacked-bar y-axis so tall stacks aren't clipped — ApexCharts
 * otherwise auto-scales a stacked axis to the largest single series, not the sum.
 */
export const niceCeil = (value: number): number | undefined => {
  if (!value || value <= 0) {
    return undefined
  }
  const mag = 10 ** Math.floor(Math.log10(value))
  const n = value / mag
  const step = n <= 1 ? 1 : n <= 1.5 ? 1.5 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10
  return step * mag
}

export const fetchMonthlyBook = (refresh = false): Promise<MonthlyBookResponse> =>
  fetchChart<MonthlyBookResponse>('delivery-dashboards/monthly-book', refresh)

export const fetchDailyGameDelivery = (refresh = false): Promise<DailyGameDeliveryResponse> =>
  fetchChart<DailyGameDeliveryResponse>('delivery-dashboards/daily-game-delivery', refresh)
